import { UntypedFormGroup } from '@angular/forms';
import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import {
  combineLatest,
  debounceTime,
  pairwise,
  startWith,
  Subscription,
} from 'rxjs';
import { LoadingService, RoiService } from '@smartflip/ui-utils';
import { propertyCategories } from '@smartflip/data-constants';
import {
  FieldConfig,
  SmartFlipUser,
  SwiperPhoto,
} from '@smartflip/data-models';
import {
  generalFields,
  ownerFields,
  mortgageFields,
} from '@smartflip/data-field-configuration';
import {
  CustomerService,
  PropertyService,
  UserService,
} from '@smartflip/helper-utils';
import { AlertDialogComponent } from '@smart-flip/shared-components';
import { confirmationDialogueData } from './general-information.constants';
import { PropertyWorkflowModalComponent } from './property-workflow-modal/property-workflow-modal.component';
import { PropertyDetailsService } from '../../property-details.service';
import { GeneralInformationService } from './general-information.service';

@Component({
  selector: 'app-general-details',
  templateUrl: './general-information.component.html',
  styleUrls: ['./general-information.component.scss'],
  standalone: false,
})
export class GeneralInformationComponent implements OnInit {
  @Input() propertyId: string;

  private maxRefreshesAllowed = 1;
  private canRefreshData = false;

  public displayAddress$ = this.propertyDetailsService.displayAddress$;
  public objectKeys = Object.keys;
  public generalForm: UntypedFormGroup;
  public ownerForm: UntypedFormGroup;
  public mortgageForm: UntypedFormGroup;
  public fieldList: FieldConfig[] = generalFields.filter(
    (field) => field.name !== 'purchasedate' && field.name !== 'solddate',
  );
  public ownerFieldList: FieldConfig[] = ownerFields;
  public mortgageFieldList: FieldConfig[] = mortgageFields;
  public imgUrl: string;
  public loading = true;
  public loanFeatureEnabled = false;
  public propertyStatus = '';
  public images: SwiperPhoto[] = [];
  public propertyStatusDate = '';
  public selectedPhotoIndex = 0;
  public showPurchaseInput = false;
  public workflowButtonText: string;

  private formListener: Subscription;

  constructor(
    private roi: RoiService,
    private propertyDetailsService: PropertyDetailsService,
    private propertyService: PropertyService,
    private generalService: GeneralInformationService,
    private modalController: ModalController,
    private router: Router,
    private loadingService: LoadingService,
    private userService: UserService,
    private customerService: CustomerService,
  ) {}

  public onSubmit(e) {
    e.preventDefault();
  }

  public setPropertyStatusDate() {
    const { solddate, purchasedate } = this.roi.formGroups['gen']?.value || {};

    if (!solddate && !purchasedate) {
      this.propertyStatusDate = '';
      return;
    }

    const newMessage =
      this.propertyStatus === 'Sold'
        ? `Sold: ${(solddate as Date).toLocaleDateString()}`
        : this.propertyStatus === 'Active'
          ? `Purchased: ${(purchasedate as Date).toLocaleDateString()}`
          : '';

    this.propertyStatusDate = newMessage;
  }

  public pullingAPIDataNotAllowed(): boolean {
    // TODO: SFP_142 Make this a paid feature
    return !!this.generalForm?.invalid;
  }

  public isDataRefreshAllowed(): boolean {
    return this.canRefreshData && this.generalForm?.valid;
  }

  public workflowProgress(editCurrentValues?: boolean) {
    // Assumes this cannot be clicked if property is already sold
    const isBuying: boolean = editCurrentValues
      ? this.propertyStatus === 'Active'
      : this.propertyStatus === 'potential';
    const { purchasedate, solddate } = this.roi.formGroups.gen.getRawValue();
    const { price, sell } = this.roi.formGroups.summary.getRawValue();

    this.openPropertyWorkflowModal({
      isBuying,
      purchasedate,
      solddate,
      price,
      sell,
    });
  }

  // Update Property Data from API source
  public async refreshGeneralData() {
    if (!this.canRefreshData) return;

    const loader = await this.loadingService.showLoading(
      'Retrieving public data...',
    );

    this.generalService.refreshPropertyData(this.propertyId);
    loader.dismiss();

    this.userService.userRefreshedDataFor(this.propertyId);
  }

  public updateThumbnailImage(newImageUrl: string) {
    this.generalService.updateThumbnailImage(newImageUrl, this.propertyId);
  }

  public togglePurchasedDate() {
    this.showPurchaseInput = !this.showPurchaseInput;
  }

  public async deleteProperty() {
    const modal = await this.modalController.create({
      component: AlertDialogComponent,
      componentProps: confirmationDialogueData,
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();

    if (data) {
      this.completePropertyDeletion(data);
    }
  }

  /**
   * name
   */
  public async viewLoanApplication() {
    const { loan } = this.propertyService.currentProperty || {};

    if (loan) {
      this.router.navigate([`/loan-application/${this.propertyId}`]);
    } else {
      this.propertyService.startLoanApplication(this.propertyId);
    }
  }

  private completePropertyDeletion(shouldContinue: boolean) {
    if (shouldContinue) {
      this.propertyService.deleteProperty(this.propertyId);
      this.router.navigate(['/home']);
    }
  }

  private setStatus() {
    const { purchasedate, solddate } = this.roi.formGroups['gen'].value;
    const { active, potential, sold } = propertyCategories;

    this.workflowButtonText = solddate
      ? sold.buttonText
      : purchasedate
        ? active.buttonText
        : potential.buttonText;
    this.propertyStatus = solddate
      ? sold.label
      : purchasedate
        ? active.label
        : potential.label;
    this.setPropertyStatusDate();
  }

  private async initializeView() {
    const loader = await this.loadingService.showLoading(
      'Retrieving public data...',
    );

    this.setStatus();
    await this.setUpForms();
    this.loading = false;
    loader.dismiss();
  }

  private async setUpForms() {
    const property = this.propertyService.currentProperty;
    const { imgUrl, images } = property;

    this.imgUrl = imgUrl;
    this.images = images?.map((imgUrl, index) => ({
      url: imgUrl,
      alt: `Home Photo-${index}`,
    }));
    this.selectedPhotoIndex =
      this.images?.length > 0
        ? this.images.findIndex((photo) => photo.url === imgUrl)
        : this.selectedPhotoIndex;
    this.generalForm = this.roi.formGroups['gen'];
    this.ownerForm = this.roi.formGroups['owner'];
    this.mortgageForm = this.roi.formGroups['mortgage'];

    if (this.formListener) {
      this.formListener.unsubscribe();
    }

    this.formListener = this.roi.formGroups['gen'].valueChanges // the generalForm updates too fast
      .pipe(startWith(null), debounceTime(800), pairwise())
      .subscribe(([prevValue, newValue]) => {
        // Update the property is set value
        this.propertyDetailsService.setProperty(
          this.roi.formGroups['gen'].valid,
        ); // assumes the only validation here is for the address

        if (
          prevValue?.solddate !== newValue?.solddate ||
          prevValue?.purchasedate !== newValue?.purchasedate
        ) {
          this.setStatus();
        }
      });
  }

  private async openPropertyWorkflowModal(data: {
    isBuying: boolean;
    purchasedate;
    solddate;
    price;
    sell;
  }): Promise<void> {
    const modal = await this.modalController.create({
      component: PropertyWorkflowModalComponent,
      componentProps: { data },
    });

    await modal.present();

    const { data: results } = await modal.onWillDismiss();

    if (results) {
      this.updatePropertyStage(results);
    }
  }

  private updatePropertyStage(results: {
    newDate: Date;
    newDollarAmount: number;
    updatePurchaseDate: boolean;
  }) {
    if (results) {
      const { updatePurchaseDate, newDate, newDollarAmount } = results;
      const updateGenData = {
        [updatePurchaseDate ? 'purchasedate' : 'solddate']: newDate,
      };
      const updateSellData = {
        [updatePurchaseDate ? 'price' : 'sell']: newDollarAmount,
      };

      this.generalForm.patchValue(updateGenData);
      this.roi.formGroups['summary'].patchValue(updateSellData);
    }
  }

  ngOnInit() {
    this.propertyDetailsService.propertyIsSet$.subscribe((propSetVal) => {
      if (propSetVal) {
        this.initializeView();
        this.setUpForms();
      }
    });

    combineLatest([
      this.userService.loggedInUser$,
      this.customerService.hasActiveSubscription$,
    ]).subscribe(([user, hasActiveSubscription]: [SmartFlipUser, boolean]) => {
      const canRefreshData =
        hasActiveSubscription ||
        user.hasRefreshedDataFor?.[this.propertyId] < this.maxRefreshesAllowed;

      this.canRefreshData = canRefreshData;
    });
  }

  ngOnChanges() {
    this.setStatus();
  }
}
